import { Outlet, RouteObject, createBrowserRouter } from "react-router-dom"
import { AdminDashboardScreen } from "~/admin/admin-dashboard-screen"
import { UsersAdminScreen } from "~/admin/users-admin-screen"
import { LocationsListScreen } from "~/locations/locations-list-screen"
import { UserLocationsScreen } from "~/locations/user-locations-screen"
import { UserPlansScreen } from "~/plans/user-plans-screen"
import { PlansScreen } from "~/plans/plans-screen"
import { PlanCreateScreen } from "~/plans/plan-create-screen"
import { PlanEditScreen } from "~/plans/plan-edit-screen"
import { LocationEditScreen } from "~/locations/location-edit-screen"
import { SignupScreen } from "~/signup/signup-screen"
import {
  RequireSystemAdminSignedIn,
  RequireUserSignedIn,
  RequireUserSignedOut,
} from "~/auth/auth-layouts"
import { LoginLayout } from "~/auth/login-layout"
import {
  credentialsLoginPath,
  dashboardPath,
  emailAuthPath,
  loginPath,
  profilePath,
  profileEditPath,
  rootPath,
  adminPath,
  adminLocationsPath,
  adminPlansPath,
  locationsPath,
  plansPath,
  signupPath,
} from "~/common/paths"
import { SidebarLayout } from "~/layouts/sidebar-layout"
import { CredentialsLoginScreen } from "~/login/credentials-login-screen"
import { EmailAuthScreen } from "~/login/email-auth-screen"
import { EmailLoginScreen } from "~/login/email-login-screen"
import { ProfileScreen } from "~/profile/profile-screen"
import { ProfileEditScreen } from "~/profile/profile-edit-screen"
import { PlanDetailsScreen } from "~/plans/plan-details-screen"
import { ErrorBoundary } from "../ui/error-boundary"
import { RootLayout } from "./root-layout"
import { RootScreen } from "./root-screen"

const systemAdminAuthenticatedRoutes: Array<RouteObject> = [
  {
    path: adminPath.pattern,
    element: <AdminDashboardScreen />,
  },
  {
    path: dashboardPath.pattern,
    element: <UsersAdminScreen />,
  },
  {
    path: adminLocationsPath.pattern + "/*",
    element: <Outlet />, // Use Outlet for nested admin location routes
    children: [
      {
        index: true, // Matches /admin/locations exactly
        element: <LocationsListScreen />,
      },
      // Add admin location edit route
      {
        path: ":id/edit",
        element: <LocationEditScreen />, // Reuse the existing edit screen
      },
    ],
  },
  {
    path: adminPlansPath.pattern + "/*",
    children: [
      {
        index: true,
        element: <PlansScreen />,
      },
      {
        path: "new",
        element: <PlanCreateScreen />,
      },
      {
        path: ":id",
        element: <PlanDetailsScreen />,
      },
      {
        path: ":id/edit",
        element: <PlanEditScreen />,
      },
    ],
  },
]

const authenticatedRoutes: Array<RouteObject> = [
  {
    path: profilePath.pattern,
    element: <ProfileScreen />,
  },
  {
    path: profileEditPath.pattern,
    element: <ProfileEditScreen />,
  },
  {
    path: locationsPath.pattern + "/*",
    element: <UserLocationsScreen />,
  },
  {
    path: plansPath.pattern + "/*",
    element: <Outlet />, // Use Outlet for nested routes
    children: [
      {
        index: true, // Matches /plans exactly
        element: <UserPlansScreen />,
      },
      // Add user-facing plan creation route
      { path: "new", element: <PlanCreateScreen /> },
      // Add user-facing plan details route
      { path: ":id", element: <PlanDetailsScreen /> },
      // Add user-facing plan edit route
      { path: ":id/edit", element: <PlanEditScreen /> },
    ],
  },
]

export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        element: <RequireSystemAdminSignedIn />,
        children: [
          {
            element: <SidebarLayout />,
            children: [
              {
                element: <Outlet />,
                children: [...systemAdminAuthenticatedRoutes],
              },
            ],
          },
        ],
      },
      {
        element: <RequireUserSignedIn />,
        children: [
          {
            element: <SidebarLayout />,
            children: [
              {
                element: <Outlet />,
                children: [...authenticatedRoutes],
              },
            ],
          },
        ],
      },
      {
        element: <RequireUserSignedOut />,
        children: [
          {
            element: <LoginLayout />,
            children: [
              {
                path: signupPath.pattern,
                element: <SignupScreen />,
              },
              {
                path: loginPath.pattern,
                element: <EmailLoginScreen />,
              },
              {
                path: credentialsLoginPath.pattern,
                element: <CredentialsLoginScreen />,
              },
              {
                path: emailAuthPath.pattern,
                element: <EmailAuthScreen />,
              },
            ],
          },
        ],
      },
      {
        path: rootPath.pattern,
        element: <RootScreen />,
      },
    ],
  },
])
